// src/hooks/useBillingSettings.ts
// Reads the centralized GST On/Off toggle (billing_settings.gst_enabled) and
// the admin-configurable GST% dropdown options (gst_percentage_options) so
// every billing entry point (OPD BillingModule/BillingStage, IPModule; NOT
// pharmacy — its GST comes from the medicine master, not a manual dropdown)
// can hide GST fields and populate the % dropdown consistently, with no
// hardcoded percentage list in a screen.
// Modeled on useBranding.ts — plain useState/useEffect so it works from both
// legacy .jsx stage components and modern .tsx screens, same as that hook.
// Unlike branding, /api/billing-settings requires auth, so this uses
// authFetch rather than a bare fetch.
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';

const DEFAULT_GST_OPTIONS = [0, 5, 12, 18];

interface BillingSettingsResponse {
  gst_enabled?: boolean;
  gst_percentage_options?: number[];
}

export interface UseBillingSettingsResult {
  gstEnabled: boolean;
  gstPercentageOptions: number[];
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useBillingSettings(): UseBillingSettingsResult {
  const [gstEnabled, setGstEnabled] = useState(true);
  const [gstPercentageOptions, setGstPercentageOptions] = useState<number[]>(DEFAULT_GST_OPTIONS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    return authFetch('/api/billing-settings')
      .then((r) => r.json())
      .then((d: BillingSettingsResponse) => {
        setGstEnabled(d.gst_enabled !== false);
        setGstPercentageOptions(Array.isArray(d.gst_percentage_options) && d.gst_percentage_options.length
          ? d.gst_percentage_options
          : DEFAULT_GST_OPTIONS);
      })
      .catch(() => {
        setGstEnabled(true);
        setGstPercentageOptions(DEFAULT_GST_OPTIONS);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { gstEnabled, gstPercentageOptions, loading, refresh };
}

export default useBillingSettings;
